import {
  useEffect,
  useReducer,
  useRef,
  useState,
  type ComponentType,
} from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import LinkExtension from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from 'tiptap-markdown'
import {
  Bold,
  Code,
  Heading2,
  Italic,
  Link,
  List,
  ListOrdered,
  SquareCode,
  Strikethrough,
  TextQuote,
} from 'lucide-react'
import { escapeRegExp, MARK_CLASS } from '@/components/Highlight'

// Markdown 要素のスタイル。リンクは常に別ウインドウで開く。
// （リンククリックで MarkdownField の編集モードに入らないよう伝播を止める）
const markdownComponents: Components = {
  a: ({ node: _node, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="text-primary-600 underline hover:text-primary-800"
    />
  ),
  ul: ({ node: _node, ...props }) => (
    <ul {...props} className="list-disc pl-5 my-1" />
  ),
  ol: ({ node: _node, ...props }) => (
    <ol {...props} className="list-decimal pl-5 my-1" />
  ),
  li: ({ node: _node, ...props }) => <li {...props} className="my-0.5" />,
  p: ({ node: _node, ...props }) => <p {...props} className="my-0.5" />,
  h1: ({ node: _node, ...props }) => (
    <h1 {...props} className="text-base font-bold my-1" />
  ),
  h2: ({ node: _node, ...props }) => (
    <h2 {...props} className="text-sm font-bold my-1" />
  ),
  h3: ({ node: _node, ...props }) => (
    <h3 {...props} className="text-sm font-semibold my-1" />
  ),
  code: ({ node: _node, ...props }) => (
    <code
      {...props}
      className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono"
    />
  ),
  pre: ({ node: _node, ...props }) => (
    <pre
      {...props}
      className="p-2 my-1 bg-gray-100 rounded text-xs font-mono overflow-x-auto"
    />
  ),
  blockquote: ({ node: _node, ...props }) => (
    <blockquote
      {...props}
      className="pl-3 my-1 border-l-2 border-gray-300 text-gray-500"
    />
  ),
  table: ({ node: _node, ...props }) => (
    <table
      {...props}
      className="my-1 border-collapse [&_th]:border [&_th]:border-gray-300 [&_th]:px-2 [&_th]:py-1 [&_td]:border [&_td]:border-gray-300 [&_td]:px-2 [&_td]:py-1"
    />
  ),
  hr: ({ node: _node, ...props }) => (
    <hr {...props} className="my-2 border-gray-200" />
  ),
  mark: ({ node: _node, ...props }) => (
    <mark {...props} className={MARK_CLASS} />
  ),
}

// hast のテキストノード内の query 一致箇所を <mark> 要素に置き換える rehype プラグイン
interface HastNode {
  type: string
  value?: string
  tagName?: string
  properties?: Record<string, unknown>
  children?: HastNode[]
}

function rehypeHighlight(options: { query: string }) {
  const pattern = new RegExp(`(${escapeRegExp(options.query)})`, 'ig')
  const walk = (node: HastNode) => {
    if (!node.children) return
    const next: HastNode[] = []
    for (const child of node.children) {
      const parts = child.type === 'text' ? child.value!.split(pattern) : null
      if (parts && parts.length > 1) {
        parts.forEach((part, i) => {
          if (!part) return
          next.push(
            i % 2 === 1
              ? {
                  type: 'element',
                  tagName: 'mark',
                  properties: {},
                  children: [{ type: 'text', value: part }],
                }
              : { type: 'text', value: part }
          )
        })
      } else {
        walk(child)
        next.push(child)
      }
    }
    node.children = next
  }
  return (tree: HastNode) => walk(tree)
}

// Markdown レンダリング（表示専用）
// remark-breaks により単一改行もそのまま改行として表示する
// highlight を渡すとレンダリング後のテキストから一致箇所をハイライトする
export function MarkdownView({
  value,
  className = '',
  highlight,
}: {
  value: string
  className?: string
  highlight?: string
}) {
  const query = highlight?.trim()
  return (
    <div className={`text-sm ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={query ? [[rehypeHighlight, { query }]] : []}
        components={markdownComponents}
      >
        {value}
      </ReactMarkdown>
    </div>
  )
}

// WYSIWYG 編集用ツールバー。カーソル位置の書式をボタンのアクティブ表示に反映する
function EditorToolbar({
  editor,
  prompting,
}: {
  editor: Editor
  prompting: React.MutableRefObject<boolean>
}) {
  const { t } = useTranslation()

  // カーソル移動・書式変更のたびに再描画してアクティブ表示を追従させる
  const [, forceUpdate] = useReducer((x) => x + 1, 0)
  useEffect(() => {
    editor.on('transaction', forceUpdate)
    return () => {
      editor.off('transaction', forceUpdate)
    }
  }, [editor])

  // リンクの設定/解除。window.prompt 中の blur で編集モードが閉じないよう prompting を立てる
  const setLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run()
      return
    }
    prompting.current = true
    const url = window.prompt(t('markdown.linkPrompt'))
    prompting.current = false
    if (!url) {
      editor.chain().focus().run()
      return
    }
    if (editor.state.selection.empty) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: url,
          marks: [{ type: 'link', attrs: { href: url } }],
        })
        .run()
    } else {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  const buttons: (
    | {
        key: string
        Icon: ComponentType<{ className?: string }>
        active: boolean
        run: () => void
      }
    | 'separator'
  )[] = [
    {
      key: 'bold',
      Icon: Bold,
      active: editor.isActive('bold'),
      run: () => editor.chain().focus().toggleBold().run(),
    },
    {
      key: 'italic',
      Icon: Italic,
      active: editor.isActive('italic'),
      run: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      key: 'strikethrough',
      Icon: Strikethrough,
      active: editor.isActive('strike'),
      run: () => editor.chain().focus().toggleStrike().run(),
    },
    'separator',
    {
      key: 'link',
      Icon: Link,
      active: editor.isActive('link'),
      run: setLink,
    },
    {
      key: 'orderedList',
      Icon: ListOrdered,
      active: editor.isActive('orderedList'),
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      key: 'bulletList',
      Icon: List,
      active: editor.isActive('bulletList'),
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    'separator',
    {
      key: 'heading',
      Icon: Heading2,
      active: editor.isActive('heading'),
      run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      key: 'quote',
      Icon: TextQuote,
      active: editor.isActive('blockquote'),
      run: () => editor.chain().focus().toggleBlockquote().run(),
    },
    {
      key: 'code',
      Icon: Code,
      active: editor.isActive('code'),
      run: () => editor.chain().focus().toggleCode().run(),
    },
    {
      key: 'codeBlock',
      Icon: SquareCode,
      active: editor.isActive('codeBlock'),
      run: () => editor.chain().focus().toggleCodeBlock().run(),
    },
  ]

  return (
    <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-gray-200 bg-gray-50 rounded-t-md">
      {buttons.map((button, i) =>
        button === 'separator' ? (
          <div key={`sep-${i}`} className="w-px h-4 bg-gray-200 mx-1" />
        ) : (
          <button
            key={button.key}
            type="button"
            // mousedown でエディタからフォーカスを奪うと blur で編集が終了してしまうため抑止
            onMouseDown={(e) => e.preventDefault()}
            onClick={button.run}
            title={t(`markdown.${button.key}`)}
            className={`p-1.5 rounded ${
              button.active
                ? 'bg-gray-200 text-gray-900'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            <button.Icon className="h-4 w-4" />
          </button>
        )
      )}
    </div>
  )
}

// WYSIWYG エディタ本体（TipTap）。入出力は従来どおり Markdown 文字列
function MarkdownEditor({
  value,
  onChange,
  placeholder,
  minRows,
  borderless,
  onFinish,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  minRows: number
  borderless: boolean
  onFinish: () => void
}) {
  const prompting = useRef(false)
  const editor = useEditor({
    extensions: [
      StarterKit,
      LinkExtension.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      // 表示側（remark-breaks）に合わせて単一改行も改行として扱う
      Markdown.configure({ breaks: true }),
    ],
    content: value,
    autofocus: 'end',
    onUpdate: ({ editor }) => onChange(editor.storage.markdown.getMarkdown()),
    onBlur: () => {
      if (!prompting.current) onFinish()
    },
  })

  if (!editor) return null

  return (
    <div
      className={
        borderless
          ? 'w-full bg-white border border-gray-300 rounded'
          : 'w-full bg-white border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent'
      }
    >
      <EditorToolbar editor={editor} prompting={prompting} />
      <EditorContent
        editor={editor}
        className="md-editor text-sm"
        style={
          {
            '--md-editor-min-height': `${minRows * 1.5 + 1}rem`,
          } as React.CSSProperties
        }
      />
    </div>
  )
}

interface MarkdownFieldProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  // 編集時エディタの最低高さ（行数換算）
  minRows?: number
  // 枠なし（テーブルセル内などに埋め込む場合）
  borderless?: boolean
}

// クリックで WYSIWYG 編集に切り替わり、フォーカスが外れると
// レンダリング表示に戻る入力フィールド
export default function MarkdownField({
  value,
  onChange,
  placeholder,
  minRows = 2,
  borderless = false,
}: MarkdownFieldProps) {
  const [editing, setEditing] = useState(false)

  if (editing) {
    return (
      <MarkdownEditor
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        minRows={minRows}
        borderless={borderless}
        onFinish={() => setEditing(false)}
      />
    )
  }

  return (
    <div
      onClick={() => setEditing(true)}
      title={placeholder}
      className={
        borderless
          ? 'w-full min-h-[2rem] px-2 py-1.5 text-sm rounded cursor-text border border-transparent hover:border-gray-200'
          : 'w-full min-h-[2.4rem] px-3 py-2 text-sm border border-gray-300 rounded-md cursor-text bg-white hover:border-gray-400'
      }
    >
      {value ? (
        <MarkdownView value={value} />
      ) : (
        <span className="text-gray-400">{placeholder ?? ''}</span>
      )}
    </div>
  )
}
